// src/hooks/menu/useMenuProducts.js
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNewMenuProducts } from '../dutchie-queries';
import { cleanMenuFilterForQuery, consoleDebug } from '../../util';
import usePricingType from '../usePricingType';
import { 
    deduplicateProducts, 
    calculatePriceRange, 
    getDisplayedProducts,
    calculateRequiredDisplayFactor 
} from '../../utils/menu/productUtils';

export const useMenuProducts = ({
    retailerId,
    selectedFilters,
    sort,
    forcedOrderType,
    cartData,
    menuFilter,
    tagSlugs,
    paginationLimit,
    specialsExist,
    filteredSpecialsList,
    selectedSpecialId,
    params,
    // Promise-based specials data readiness
    waitForSpecialsData,
    isDataReady
}) => {
    const [queryArgs, setQueryArgs] = useState(null);
    const [productDisplayFactor, setProductDisplayFactor] = useState(1);
    const [displaySpecialsList, setDisplaySpecialsList] = useState(false);
    const [priceRange, setPriceRange] = useState(null);
    const [initialQuerySet, setInitialQuerySet] = useState(false);
    const displayFactorRef = useRef(productDisplayFactor);
    const [pricingType] = usePricingType(retailerId);

    // Build query arguments
    const getAndSetQueryArgs = useCallback(() => {
        const { sortDir, sortKey } = sort;
        const menuTypeParam = forcedOrderType?.menuType;
        
        // Use the pricingType from the hook if cartData isn't ready yet
        let effectivePricingType = cartData?.pricingType || pricingType || 'RECREATIONAL';
        
        if (menuTypeParam) {
            effectivePricingType = menuTypeParam.toLowerCase() === "rec" ? "RECREATIONAL" : 
                                  (menuTypeParam.toLowerCase() === "med" ? "MEDICAL" : effectivePricingType);
        }

        let args = null;
        
        if (selectedFilters?.custom_section) {
            args = {
                queryName: 'MenuByCustomSection',
                variables: {
                    retailerId,
                    menuSectionFilterType: 'CUSTOM_SECTION',
                    menuSectionFilterName: selectedFilters.custom_section,
                    menuType: effectivePricingType,
                    sortDir,
                    sortKey
                }
            };
        } else if (selectedFilters?.staff_picks) {
            args = {
                queryName: 'MenuByStaffPicks',
                variables: {
                    retailerId,
                    menuSectionFilterType: 'STAFF_PICKS',
                    menuType: effectivePricingType,
                    sortDir,
                    sortKey
                }
            };
            setDisplaySpecialsList(false);
        } else if (selectedFilters?.specials) {
            // Build query with available data - don't wait for promise resolution
            // This allows products to start loading immediately
            const specialIds = selectedSpecialId
                ? [selectedSpecialId]
                : (filteredSpecialsList?.length > 0 ? filteredSpecialsList.map((s) => s.id) : []);

            args = {
                queryName: 'MenuBySpecialsSection',
                variables: {
                    retailerId,
                    menuFilter: {
                        ...cleanMenuFilterForQuery(selectedFilters, menuFilter, tagSlugs?.[retailerId]),
                        menuSection: {
                            type: 'SPECIALS',
                            specialId: specialIds
                        }
                    },
                    menuType: effectivePricingType,
                    sortDir,
                    sortKey
                }
            };
            setDisplaySpecialsList(Boolean(specialsExist && !params?.specialCategoryName));
        } else if (selectedFilters) {
            args = {
                queryName: 'MenuQuery',
                variables: {
                    retailerId,
                    menuFilter: cleanMenuFilterForQuery(selectedFilters, menuFilter, tagSlugs?.[retailerId]),
                    menuType: effectivePricingType,
                    sortDir,
                    sortKey
                }
            };
            setDisplaySpecialsList(false);
        }

        setQueryArgs(args);
        if (args && !initialQuerySet) {
            setInitialQuerySet(true);
        }
    }, [sort, forcedOrderType, cartData?.pricingType, pricingType, selectedFilters, retailerId, 
        specialsExist, filteredSpecialsList, menuFilter, tagSlugs, selectedSpecialId, 
        params?.specialCategoryName, isDataReady, waitForSpecialsData]);

    // Fetch products using Apollo hook
    const {
        allProducts,
        totalProductsCount,
        fetchNextPage: fetchNextPageProductsData,
        hasNextPage: hasNextPageProductsData,
        isLoading: isLoadingProducts,
        isFetchingNextPage: isFetchingNextPageProductsData,
        error: productsError,
        refetch: refetchProductsData,
    } = useNewMenuProducts(retailerId, queryArgs, {
        enabled: Boolean(queryArgs !== null && retailerId),
        apiPageSize: paginationLimit,
    });

    // Deduplicate products
    const allUniqueFetchedProducts = useMemo(() => {
        return deduplicateProducts(allProducts);
    }, [allProducts]);

    // Calculate displayed products
    const processedProducts = useMemo(() => {
        return getDisplayedProducts(allUniqueFetchedProducts, productDisplayFactor, paginationLimit);
    }, [allUniqueFetchedProducts, productDisplayFactor, paginationLimit]);

    // Calculate price range
    useEffect(() => {
        const range = calculatePriceRange(allUniqueFetchedProducts, pricingType);
        setPriceRange(range);
    }, [allUniqueFetchedProducts, pricingType]);

    // Update query args when dependencies change - don't wait for cartData
    useEffect(() => {
        // Set up initial query even without cart data
        if (retailerId && !initialQuerySet) {
            getAndSetQueryArgs();
        } else if (cartData?.pricingType && initialQuerySet) {
            // Update query when cart data becomes available
            getAndSetQueryArgs();
        }
    }, [retailerId, cartData?.pricingType, selectedFilters, sort, selectedSpecialId, 
        forcedOrderType, getAndSetQueryArgs, initialQuerySet]);

    // Stable setter for display factor
    const setDisplayFactorRef = useCallback((factor) => {
        displayFactorRef.current = factor;
        setProductDisplayFactor(factor);
    }, []);
    
    return {
        queryArgs,
        setQueryArgs,
        allProducts,
        allUniqueFetchedProducts,
        processedProducts,
        totalProductsCount,
        totalUniqueItemsFetched: allUniqueFetchedProducts.length,
        fetchNextPageProductsData,
        hasNextPageProductsData,
        isLoadingProducts,
        isFetchingNextPageProductsData,
        productsError,
        refetchProductsData,
        productDisplayFactor,
        displayFactorRef,
        setDisplayFactorRef,
        displaySpecialsList,
        setDisplaySpecialsList,
        priceRange,
        setPriceRange
    };
};
